import React, { useState, useEffect } from "react";
import axios from "axios";
import Img from "gatsby-image";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import { useReviews } from "../../hooks/useReviews";
import Container from "../Container/container";
import { Stars, StarsInput } from "../Stars/stars";
import CTA from "../CTA/cta";
import "./reviews.scss";

const getFirstLetter = (name) => {
	return name.charAt(0);
};

const Reviews = ({
	product = null,
	scrollToReviews = () => null,
	initialData = {},
	writeOnly = false,
	products = [],
}) => {
	const domain = process.env.GATSBY_MYSHOPIFY_URL || "default";

	const { data: reviews } = useReviews(
		product?.legacyResourceId,
		initialData
	);

	const [displayImage, setDisplayImage] = useState(null);
	const [writeReview, setWriteReview] = useState(false);
	const [previewImages, setPreviewImages] = useState([]);
	const [displayProduct, setDisplayProduct] = useState(product);
	const [reviewLimit, setReviewLimit] = useState(5);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState,
		formState: { errors },
		reset,
	} = useForm({
		defaultValues: {
			rating: 5,
			media: [],
		},
	});

	useEffect(() => {
		register("media", { required: false });
	}, []);

	useEffect(() => {
		const productId = watch("product");
		const product = products.find((p) => p.legacyResourceId === productId);

		if (product) {
			setDisplayProduct(product);
		}
	}, [watch("product")]);

	if (domain === "tearriblesau.myshopify.com") {
		return null;
	}

	const onSubmit = async (data) => {
		const {
			heading,
			body,
			email,
			name,
			media,
			rating,
			product: selectedProduct,
		} = data;

		const payload = {
			heading,
			body,
			state: "pending_approval",
			external_product_id: selectedProduct || product?.legacyResourceId,
			customer: {
				name,
				email,
			},
			rating,
			media,
		};

		const headers = {
			"Content-Type": "application/json",
		};

		return axios
			.post(
				"https://iiedukuha8.execute-api.us-east-1.amazonaws.com/reviews",
				payload,
				{
					headers,
				}
			)
			.catch((error) => {
				console.log(error);
			});
	};

	const fluid =
		displayProduct?.featuredImage?.localFile?.childImageSharp?.fluid;

	const handleSelectImage = (event) => {
		const file = event.target.files[0];
		if (!file) return;

		const fileReader = new FileReader();
		fileReader.addEventListener("load", () => {
			setPreviewImages((prev) => [...prev, fileReader.result]);
			setValue("media", [...previewImages, fileReader.result]);
		});
		fileReader.readAsDataURL(file);
	};

	if (formState.isSubmitSuccessful) {
		return (
			<Container style={{ marginTop: "4.8rem" }}>
				<h2 className="reviews__title">Write a Review</h2>
				<p style={{ textAlign: "center" }}>
					Thank you for your review!
				</p>
				<div
					style={{
						justifyContent: "center",
						display: "flex",
						gap: "1.6rem",
					}}
				>
					<CTA
						title="Go Back"
						secondary
						styles={{ marginBottom: 0 }}
						onClick={() => {
							reset();
							setDisplayProduct(null);
							setPreviewImages([]);
							setWriteReview(false);
						}}
					/>
				</div>
			</Container>
		);
	}

	if (writeReview || writeOnly) {
		return (
			<>
				{displayImage && (
					<div
						className="reviews__overlay"
						onClick={() => setDisplayImage(null)}
					>
						<div className="reviews__image__container">
							<button
								className="reviews__button__close"
								onClick={() => setDisplayImage(null)}
							>
								Close &nbsp;
								<FontAwesomeIcon icon={faXmark} />
							</button>
							<img
								src={displayImage}
								className="reviews__image"
							/>
						</div>
					</div>
				)}
				<Container style={{ marginTop: "4.8rem" }}>
					<h2 className="reviews__title">Write a Review</h2>
					<div className="reviews__write reviews" id="reviews">
						<div className="reviews__product-info">
							<h2
								className="product-detail__title"
								style={{ fontSize: "1.6rem" }}
							>
								{displayProduct?.title}
							</h2>
							<Img
								fluid={fluid}
								className="reviews__product-image"
							/>
						</div>
						<form
							onSubmit={handleSubmit(onSubmit)}
							className="reviews__main"
						>
							{product === null && (
								<>
									<label className="input-label">
										Product
									</label>
									{errors.heading && (
										<p className="reviews__error">
											Please select a product.
										</p>
									)}
									<select
										type="text"
										placeholder="Select a product"
										{...register("product", {
											required: true,
										})}
									>
										<option value="">
											Select a product
										</option>
										{products.map((product) => (
											<option
												value={product.legacyResourceId}
												key={product.legacyResourceId}
											>
												{product.title}
											</option>
										))}
									</select>
								</>
							)}
							<label className="input-label">Title</label>
							{errors.heading && (
								<p className="reviews__error">
									Please enter a title.
								</p>
							)}
							<input
								type="text"
								placeholder="Title"
								{...register("heading", { required: true })}
							/>
							<label className="input-label">Rating</label>
							<StarsInput
								currentValue={watch("rating")}
								setValue={setValue}
								style={{ marginBottom: "1.6rem" }}
							/>
							<label className="input-label">Body</label>
							{errors.body && (
								<p className="reviews__error">
									Please enter a body for the review.
								</p>
							)}
							<textarea
								placeholder=""
								{...register("body", { required: true })}
							/>
							<label className="file-upload">
								Upload Images (Optional)
								<input
									type="file"
									name="image-upload"
									onChange={handleSelectImage}
								/>
							</label>
							{previewImages.length > 0 && (
								<div
									className="reviews__media"
									style={{ marginBottom: "24px" }}
								>
									{previewImages.map((photo, index) => {
										return (
											<button
												key={`preview-input-${index}`}
												className="reviews__media__button"
												onClick={() =>
													setDisplayImage(photo)
												}
											>
												<img
													key={`preview__${index}`}
													src={photo}
													alt={`preview image ${index}`}
													className="reviews__media__image"
												/>
											</button>
										);
									})}
								</div>
							)}
							<label className="input-label">Your Name</label>
							{errors.name && (
								<p className="reviews__error">
									Please enter your name.
								</p>
							)}
							<input
								type="text"
								{...register("name", { required: true })}
							/>
							<label className="input-label">Your Email</label>
							{errors.email && (
								<p className="reviews__error">
									Please enter your email.
								</p>
							)}
							<input
								type="text"
								placeholder="example@tearribles.com"
								{...register("email", { required: true })}
								style={{ marginBottom: 0 }}
							/>
							<p
								style={{
									marginTop: 0,
									opacity: 0.8,
									fontSize: "12px",
								}}
							>
								* Email is for verification purposes only and
								will not be displayed.
							</p>
							<div
								style={{
									justifyContent: "flex-end",
									display: "flex",
									gap: "1.6rem",
								}}
							>
								<CTA
									title="Cancel"
									secondary
									styles={{ marginBottom: 0 }}
									onClick={() => {
										setWriteReview(false);
									}}
								/>
								<button
									type="submit"
									className="cta cta--primary"
									style={{ marginBottom: 0, border: 0 }}
								>
									{formState.isSubmitting
										? "...Submitting"
										: "Submit Review"}
								</button>
							</div>
						</form>
					</div>
				</Container>
			</>
		);
	}

	return (
		<>
			{displayImage && (
				<div
					className="reviews__overlay"
					onClick={() => setDisplayImage(null)}
				>
					<div className="reviews__image__container">
						<button
							className="reviews__button__close"
							onClick={() => setDisplayImage(null)}
						>
							Close &nbsp;
							<FontAwesomeIcon icon={faXmark} />
						</button>
						<img src={displayImage} className="reviews__image" />
					</div>
				</div>
			)}
			<Container style={{ marginTop: "4.8rem" }}>
				<h2 className="reviews__title">Reviews</h2>
				<div className="reviews">
					{reviews?.data &&
						reviews.data.length > 0 &&
						reviews.data
							.filter((_, index) => {
								return index < reviewLimit;
							})
							.map((review) => {
								const userFirstLetter = getFirstLetter(
									review.customer.display_name
								);

								const createdAt = new Date(review.created_at);

								return (
									<div
										key={review.id}
										className="reviews__review"
									>
										<div className="reviews__user">
											{/* <div className="reviews__user__avatar">
											{userFirstLetter}
										</div> */}
											<div className="reviews__user__info">
												<div className="reviews__user__name">
													{
														review.customer
															.display_name
													}
												</div>
												<div className="reviews__user__location">
													{
														review.customer
															.display_location
													}
												</div>
											</div>
										</div>
										<div className="reviews__main">
											<div className="reviews__stars">
												<Stars rating={review.rating} />
												<p className="reviews__date">
													{createdAt.toLocaleDateString(
														"en-US",
														{
															year: "numeric",
															month: "long",
															day: "numeric",
														}
													)}
												</p>
											</div>
											<p className="reviews__heading">
												{review.heading}
											</p>
											<p>{review.body}</p>
											{review.media.length > 0 && (
												<div className="reviews__media">
													{review.media.map(
														(media) => {
															if (
																media.type !==
																"photo"
															) {
																return null;
															}

															return (
																<button
																	key={`preview-${media.id}`}
																	className="reviews__media__button"
																	onClick={() =>
																		setDisplayImage(
																			media.url
																		)
																	}
																>
																	<img
																		key={
																			media.id
																		}
																		src={
																			media.thumbnail_url
																		}
																		alt={
																			media.caption
																		}
																		className="reviews__media__image"
																	/>
																</button>
															);
														}
													)}
												</div>
											)}
										</div>
									</div>
								);
							})}
				</div>
				{reviews?.meta?.total_count &&
				reviewLimit < reviews?.meta?.total_count ? (
					<div
						style={{
							justifyContent: "center",
							display: "flex",
						}}
					>
						<CTA
							title="See More Reviews..."
							inverted
							styles={{
								marginBottom: 0,
							}}
							onClick={() => {
								setReviewLimit((prev) => prev + 5);
							}}
						/>
					</div>
				) : null}
				<div style={{ justifyContent: "center", display: "flex" }}>
					<CTA
						title="Write a Review"
						secondary
						styles={{
							marginBottom: 0,
						}}
						onClick={() => {
							setWriteReview(true);
							scrollToReviews();
						}}
					/>
				</div>
			</Container>
		</>
	);
};

export default Reviews;
